// Day/night environment driven by the player's LOCAL clock. Phase 1: smoothly
// animate the scene's ambient + sun light (color, intensity, elevation) and the
// page's sky gradient across the 24-hour cycle, so midnight is played under a
// dark blue night and noon under bright daylight — with golden dawn/dusk in
// between. Later phases add glowing lanterns + window lights at night.
//
// Keyframes are spaced around the clock; the current fractional hour lerps
// between the two bracketing frames, so logging in at 6:40pm shows actual dusk
// rather than a hard state flip.

import * as THREE from 'three'
import { setNight } from './nightLights.js'
import { getSetting } from '../game/settings.js'

// When the day/night setting is off, the world stays at a fixed pleasant
// afternoon (bright, glows off) instead of following the local clock.
const DAYTIME_HOUR = 13

// Each keyframe: h = hour (0–24); sun/amb = light colors (hex); sunI/ambI =
// intensities; el = sun elevation 0 (horizon) → 1 (overhead); n = night factor
// 0 (day, glows off) → 1 (night, lanterns/windows lit); sky = 3-stop CSS
// gradient (top → horizon → bottom) shown behind the transparent canvas.
const KEYS = [
  { h: 0,    sun: 0x7585c0, sunI: 0.56, amb: 0x55629a, ambI: 0.88, el: 0.42, n: 1.00, sky: ['#101736', '#1d2a52', '#34416a'] }, // deep night (moonlit)
  { h: 5,    sun: 0x7280b6, sunI: 0.56, amb: 0x55619a, ambI: 0.86, el: 0.30, n: 0.90, sky: ['#1c2750', '#39426c', '#665c72'] }, // pre-dawn
  { h: 6.5,  sun: 0xff9d63, sunI: 0.70, amb: 0x9a7f78, ambI: 0.52, el: 0.26, n: 0.45, sky: ['#ffb277', '#ff9b76', '#7d6a8e'] }, // sunrise
  { h: 9,    sun: 0xfff0c4, sunI: 1.00, amb: 0xdfe6f2, ambI: 0.56, el: 0.62, n: 0.00, sky: ['#bfe2ff', '#a4d0f6', '#e2ecf4'] }, // morning
  { h: 12,   sun: 0xfff7e8, sunI: 1.15, amb: 0xeaf2ff, ambI: 0.60, el: 0.96, n: 0.00, sky: ['#a9dcff', '#cde9ff', '#eef6ff'] }, // noon
  { h: 15.5, sun: 0xfff0c4, sunI: 1.04, amb: 0xe8effa, ambI: 0.57, el: 0.66, n: 0.00, sky: ['#bfe1ff', '#cde6f6', '#eaf2f8'] }, // afternoon
  { h: 18,   sun: 0xff8742, sunI: 0.82, amb: 0x9a7466, ambI: 0.52, el: 0.27, n: 0.30, sky: ['#ffb061', '#ff7c4f', '#6a5570'] }, // golden / sunset
  { h: 20,   sun: 0x6a5a92, sunI: 0.42, amb: 0x46406a, ambI: 0.46, el: 0.20, n: 0.75, sky: ['#3a2f57', '#473a62', '#564a6c'] }, // dusk
  { h: 22,   sun: 0x7282bc, sunI: 0.56, amb: 0x54609a, ambI: 0.87, el: 0.38, n: 1.00, sky: ['#16204a', '#28335e', '#3a4870'] }, // night (moonlit)
  { h: 24,   sun: 0x7585c0, sunI: 0.56, amb: 0x55629a, ambI: 0.88, el: 0.42, n: 1.00, sky: ['#101736', '#1d2a52', '#34416a'] }, // wrap = h0
]

const lerp = (a, b, t) => a + (b - a) * t

// A fixed full-screen layer of stars + a moon, sitting behind the transparent
// canvas (above the body sky gradient). Its opacity is driven by the night
// factor so the stars fade in after dark and vanish by day. Built once.
function buildNightSky() {
  if (typeof document === 'undefined') return null
  const existing = document.getElementById('sky-night')
  if (existing) return existing
  const el = document.createElement('div')
  el.id = 'sky-night'
  el.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:-1;opacity:0'
  const stars = []
  for (let i = 0; i < 120; i++) {
    const x = (Math.random() * 100).toFixed(2)
    const y = (Math.random() * 70).toFixed(2) // upper sky only
    const a = (0.35 + Math.random() * 0.6).toFixed(2)
    const s = Math.random() < 0.16 ? 1.7 : 1.0 // a few brighter stars
    stars.push(`radial-gradient(${s}px ${s}px at ${x}% ${y}%, rgba(255,255,255,${a}) 0%, rgba(255,255,255,0) 60%)`)
  }
  el.style.backgroundImage = stars.join(',')
  el.style.backgroundRepeat = 'no-repeat'
  const moon = document.createElement('div')
  moon.style.cssText = [
    'position:absolute', 'top:8%', 'right:15%', 'width:84px', 'height:84px', 'border-radius:50%',
    'background:radial-gradient(circle at 38% 38%, #fdf7e6 0%, #ece3c4 56%, rgba(236,227,196,0.25) 73%, rgba(236,227,196,0) 80%)',
    'box-shadow:0 0 46px 14px rgba(245,240,205,0.32)',
  ].join(';')
  el.appendChild(moon)
  document.body.appendChild(el)
  return el
}

export function createDayNight({ ambient, sun }) {
  const skyNight = buildNightSky()
  let lastNight = -1
  let currentNight = 0 // last computed night factor (0 day … 1 night), for postfx
  const _sunC = new THREE.Color()
  const _ambC = new THREE.Color()
  const _tmp = new THREE.Color()
  const _s1 = new THREE.Color()
  const _s2 = new THREE.Color()
  let lastGrad = ''
  let forcedHour = null // dev/testing override; null = use real local time

  const mixHex = (h1, h2, t) => '#' + _s1.set(h1).lerp(_s2.set(h2), t).getHexString()

  function bracket(hour) {
    for (let i = 0; i < KEYS.length - 1; i++) {
      if (hour >= KEYS[i].h && hour <= KEYS[i + 1].h) {
        const a = KEYS[i]
        const b = KEYS[i + 1]
        return { a, b, t: (hour - a.h) / ((b.h - a.h) || 1) }
      }
    }
    return { a: KEYS[0], b: KEYS[0], t: 0 }
  }

  function currentHour() {
    if (forcedHour != null) return forcedHour // dev override
    if (!getSetting('nightMode')) return DAYTIME_HOUR // toggle off → permanent day
    const d = new Date()
    return d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600
  }

  // Recompute + apply the environment for the current (or forced) local hour.
  function tick() {
    const { a, b, t } = bracket(currentHour())

    sun.color.copy(_sunC.setHex(a.sun).lerp(_tmp.setHex(b.sun), t))
    sun.intensity = lerp(a.sunI, b.sunI, t)
    ambient.color.copy(_ambC.setHex(a.amb).lerp(_tmp.setHex(b.amb), t))
    ambient.intensity = lerp(a.ambI, b.ambI, t)

    // Move the sun across the sky by elevation (kept on a fixed azimuth so the
    // low golden-hour rake reads, while noon lights from overhead).
    const el = lerp(a.el, b.el, t)
    sun.position.set(6, 1.5 + el * 11, 4)

    // Fade the warm lantern/window glows + the star layer in at night.
    const nightN = lerp(a.n, b.n, t)
    currentNight = nightN
    setNight(nightN)
    if (skyNight && Math.abs(nightN - lastNight) > 0.02) {
      skyNight.style.opacity = nightN.toFixed(2)
      lastNight = nightN
    }

    // Sky gradient — only touch the DOM when the rounded color actually changes.
    const grad = `linear-gradient(180deg, ${mixHex(a.sky[0], b.sky[0], t)} 0%, `
      + `${mixHex(a.sky[1], b.sky[1], t)} 45%, ${mixHex(a.sky[2], b.sky[2], t)} 100%)`
    if (grad !== lastGrad) {
      document.body.style.background = grad
      lastGrad = grad
    }
  }

  // Dev/testing: force a specific hour (0–24), or pass null to resume real time.
  function setHour(h) { forcedHour = h == null ? null : ((h % 24) + 24) % 24; tick() }

  tick() // apply immediately at boot so there's never a flash of the wrong sky
  return { tick, setHour, night: () => currentNight }
}
